/**
 * Owner-view Knowledge tab body (tab 4 of 4).
 *
 * A calm, read-only window into what the genie knows: where its knowledge comes
 * from (website, docs, price list, hours) as one quiet container of source rows
 * with a green "Refreshed …" line, then a short preview of things it can answer,
 * then the single mutating door in this flow — the "Tell our genie" concierge
 * banner. Nothing on this surface is editable; to teach the genie something new,
 * the owner tells our genie.
 *
 * Rendered inside GenieOwnerDetail (receives NO props — genie id via useParams).
 * Matches the OverviewTab SectionCard rhythm so the 4 tabs feel like one surface.
 *
 * Data: stub `useGenieKnowledge(id)` (read-only). Brand law: genie tokens only;
 * signature gradient for the concierge banner; wireframe green -> genie-success;
 * "Help Genie" two words; no "AI" language; no em dashes.
 */
import { useParams } from "react-router-dom";
import {
  Check,
  FileText,
  Globe,
  HelpCircle,
  type LucideIcon,
  Sparkles,
  StickyNote,
} from "lucide-react";
import { SectionCard } from "@/components/voice-genie/atoms";
import { useGenieKnowledge } from "@/hooks/voice-vision";
import type {
  KnowledgeSource,
  KnowledgeSourceKind,
} from "@/types/voice-vision";

const BLUE = "#4E9CFF";
const PINK = "#FF4DB8";

/* ───────────────────────── kind → icon + tint ───────────────────────── */

const KIND_ICON: Record<KnowledgeSourceKind, LucideIcon> = {
  website: Globe,
  pdf: FileText,
  doc: FileText,
  faq: HelpCircle,
  note: StickyNote,
};

const KIND_TINT: Record<KnowledgeSourceKind, string> = {
  website: BLUE,
  pdf: PINK,
  doc: "#FFBB33",
  faq: PINK,
  note: "#FFBB33",
};

/** Plain-language "Refreshed …" line from an ISO timestamp. */
function refreshedLabel(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "Refreshed recently";
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return "Refreshed today";
  if (days === 1) return "Refreshed yesterday";
  if (days < 7) return `Refreshed ${days} days ago`;
  if (days < 14) return "Refreshed last week";
  if (days < 31) return `Refreshed ${Math.floor(days / 7)} weeks ago`;
  return "Refreshed last month";
}

/* ─────────────────────────── source row ─────────────────────────────── */

const KnowledgeSourceRow = ({
  source,
  first,
}: {
  source: KnowledgeSource;
  first: boolean;
}) => {
  const Icon = KIND_ICON[source.kind] ?? FileText;
  const tint = KIND_TINT[source.kind] ?? BLUE;

  return (
    <div
      className={`flex items-center gap-3.5 px-5 md:px-6 py-3.5 ${
        first ? "" : "border-t border-[#F3F4F6]"
      }`}
    >
      <div
        className="w-[34px] h-[34px] rounded-[10px] flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${tint}1a` }}
      >
        <Icon className="w-[17px] h-[17px]" style={{ color: tint }} />
      </div>

      <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-baseline sm:gap-2.5">
        <span className="text-sm font-bold text-[#222B45] truncate flex-shrink-0">
          {source.name}
        </span>
        <span className="text-[13px] text-gray-500 truncate min-w-0">
          {source.meta}
        </span>
      </div>

      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className="w-[6px] h-[6px] rounded-full bg-genie-success" />
        <span className="text-xs font-semibold text-gray-500 hidden sm:inline">
          {refreshedLabel(source.syncedAt)}
        </span>
      </div>
    </div>
  );
};

/* ─────────── derive a short "things it can answer" preview ───────────── */

function thingsItKnows(coverageNote?: string): string[] {
  if (!coverageNote) return [];
  // "Covers services, pricing, opening hours and bookings." -> 4 facts
  const cleaned = coverageNote
    .replace(/^covers\s+/i, "")
    .replace(/\.$/, "")
    .replace(/\s+and\s+/i, ", ");
  const topics = cleaned
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return topics.map((topic) => {
    const t = topic.charAt(0).toUpperCase() + topic.slice(1);
    return `Questions about ${t.toLowerCase()}`;
  });
}

/* ─────────────────────────── loading state ──────────────────────────── */

const KnowledgeSkeleton = () => (
  <div className="w-full">
    <div className="h-3 w-52 rounded bg-gray-100 animate-pulse mb-3" />
    <div className="rounded-[18px] border border-genie-border bg-white shadow-sm overflow-hidden">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className={`flex items-center gap-3.5 px-5 md:px-6 py-3.5 ${
            i === 0 ? "" : "border-t border-[#F3F4F6]"
          }`}
        >
          <div className="w-[34px] h-[34px] rounded-[10px] bg-gray-100 animate-pulse flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="h-3.5 w-1/3 rounded bg-gray-100 animate-pulse" />
            <div className="h-2.5 w-1/2 rounded bg-gray-100 animate-pulse mt-2" />
          </div>
          <div className="h-2.5 w-20 rounded bg-gray-100 animate-pulse" />
        </div>
      ))}
    </div>
    <div className="mt-[18px] p-[1.5px] rounded-[18px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm">
      <div className="rounded-[16.5px] bg-white px-5 md:px-6 py-5">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`flex items-center gap-3 py-3 ${
              i === 0 ? "" : "border-t border-[#F3F4F6]"
            }`}
          >
            <div className="w-[22px] h-[22px] rounded-full bg-gray-100 animate-pulse flex-shrink-0" />
            <div className="h-3.5 w-2/3 rounded bg-gray-100 animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ──────────────────────────────── tab ───────────────────────────────── */

const KnowledgeTab = () => {
  const { id = "" } = useParams();
  const { data, isLoading, isError } = useGenieKnowledge(id);

  if (isLoading) return <KnowledgeSkeleton />;

  if (isError) {
    return (
      <div className="rounded-[18px] border border-genie-border bg-white shadow-sm flex flex-col items-center text-center py-16 px-6">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
          style={{ backgroundColor: `${PINK}1a` }}
        >
          <FileText className="w-6 h-6" style={{ color: PINK }} />
        </div>
        <h2 className="text-base font-bold text-[#222B45]">
          We couldn&apos;t load what your genie knows
        </h2>
        <p className="text-sm text-gray-500 mt-1 max-w-[360px]">
          Please try again in a moment.
        </p>
      </div>
    );
  }

  const sources = data?.sources ?? [];
  const facts = thingsItKnows(data?.coverageNote);

  return (
    <div className="w-full">
      {/* ── Where its knowledge comes from — one calm container of rows ── */}
      <p className="text-[12px] font-bold tracking-[0.6px] uppercase text-gray-500 mb-3">
        Where its knowledge comes from
      </p>

      {sources.length === 0 ? (
        <div className="rounded-[18px] border border-genie-border bg-white shadow-sm flex flex-col items-center text-center py-16 px-6">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3"
            style={{ backgroundColor: `${BLUE}1a` }}
          >
            <FileText className="w-6 h-6" style={{ color: BLUE }} />
          </div>
          <h2 className="text-base font-bold text-[#222B45]">
            No knowledge sources yet
          </h2>
          <p className="text-sm text-gray-500 mt-1 max-w-[380px]">
            Tell our genie about your business and we will give it the documents
            and notes it needs to answer your callers.
          </p>
        </div>
      ) : (
        <div className="rounded-[18px] border border-genie-border bg-white shadow-sm overflow-hidden">
          {sources.map((s, i) => (
            <KnowledgeSourceRow key={s.id} source={s} first={i === 0} />
          ))}
        </div>
      )}

      {/* ── A few things it can answer ── */}
      {facts.length > 0 && (
        <SectionCard
          genieIcon="book-open"
          tint={BLUE}
          title="A few things it can answer"
          subtitle={data?.coverageNote ?? "Drawn from the sources above"}
          className="mt-[18px]"
        >
          <div className="flex flex-col">
            {facts.map((fact, i) => (
              <div
                key={fact}
                className={`flex items-center gap-3 py-3 ${
                  i === 0 ? "" : "border-t border-[#F3F4F6]"
                }`}
              >
                <span
                  className="w-[22px] h-[22px] rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: "rgba(78,156,255,0.12)" }}
                >
                  <Check
                    className="w-[13px] h-[13px] text-genie-success"
                    strokeWidth={3}
                  />
                </span>
                <span className="flex-1 min-w-0 text-sm text-[#222B45]">
                  {fact}
                </span>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* ── Teach it something new -> the concierge door ── */}
      <button
        type="button"
        className="w-full text-left mt-[18px] rounded-[18px] bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg shadow-genie-primary/30 px-5 md:px-6 py-[18px] flex items-center gap-4 hover:opacity-95 transition-all"
      >
        <span className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-white" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[15.5px] font-extrabold text-white">
            Want it to know something new?
          </span>
          <span className="block text-[13px] text-white/90 mt-0.5">
            Tell our genie what to add or change and we will update its knowledge
            for you.
          </span>
        </span>
        <span className="flex-shrink-0 text-white text-[13px] font-bold inline-flex items-center gap-1.5">
          Tell our genie
          <Sparkles className="w-4 h-4" />
        </span>
      </button>
    </div>
  );
};

export default KnowledgeTab;
